import React from 'react';

import {
  Grid,
  Button,
  styled,
  Typography
} from '@mui/material';
import {
  authService,
  BaseOnboardingTemplate,
  fields,
  imageUtil,
  ResponsiveImage,
  routeDirectory
} from 'modules';

import { useNavigate } from 'react-router-dom';
import * as dataService from './dataService';
import _ from 'lodash';



const PasswordRequirementsTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(2)
}));

const imgsPerson = imageUtil.getUrlSet('person', 'png');
const imgsMail = imageUtil.getUrlSet('mail', 'png');
const imgsPassword = imageUtil.getUrlSet('password', 'png');



const ControlledTextField = fields.genTextFieldControllerComponent<dataService.IFormData>();

export interface ISignUpActivityProps { }

export const SignUpActivity: React.FC<ISignUpActivityProps> = props => {
  const navigate = useNavigate();
  const {
    form,
    loading,
    queries,
    mutations
  } = dataService.useSignUpActivity();



  async function processSubmit(data: dataService.IFormData) {
    const isEmailNotInUse = await queries.verifyEmailNotInUse(data.email);
    if (!isEmailNotInUse) {
      alert('This email has been taken. Please use another one.');
      return;
    }

    try {
      await mutations.register({
        email: data.email,
        password: data.password
      });
      await authService.signInWithRegisteredPassword(data.email);

      // This code is wrapped in a defer because the above call to sign in triggers
      // an application-wide render. In flight requests are cancelled, and the call
      // to getLoggedInUser is cancelled. The code after it never runs.
      _.defer(async () => {
        const res = await queries.getLoggedInUser();
        if (res.data.loggedInUser) {
          await mutations.saveSiteUser({
            userId: res.data.loggedInUser.id,
            firstName: data.firstName,
            lastName: data.lastName
          });
          navigate(routeDirectory.home.path());

        } else {
          alert('There was an error logging in and fetching the user account.');
        }
      });

    } catch (e) {
      if ((e as Error).name === 'UsernameExistsException') {
        alert('This email has been taken. Please use another one.');
      } else {
        alert(e);
      }
    }
  }



  return (
    <BaseOnboardingTemplate.FC
      stepperMode="signUp"
      title="Sign Up"
      subtitle="First, let's create your account by entering the following information">
      <form onSubmit={form.handleSubmit(processSubmit)}>
        <Grid
          container
          spacing={7}
          rowSpacing={3}>
          <Grid
            item
            xs={6}>
            <ControlledTextField
              startAdornment={<ResponsiveImage.FC urlSet={imgsPerson} />}
              isDisabled={loading}
              fieldKey="firstName"
              label="First Name"
              formControl={form.control} />
          </Grid>

          <Grid
            item
            xs={6}>
            <ControlledTextField
              startAdornment={<ResponsiveImage.FC urlSet={imgsPerson} />}
              isDisabled={loading}
              fieldKey="lastName"
              label="Last Name"
              formControl={form.control} />
          </Grid>

          <Grid
            item
            xs={12}>
            <ControlledTextField
              startAdornment={<ResponsiveImage.FC urlSet={imgsMail} />}
              isDisabled={loading}
              fieldKey="email"
              label="Email"
              formControl={form.control} />
          </Grid>

          <Grid
            container
            item
            justifyContent="center">
            <Grid container item spacing={7}>
              <Grid
                item
                xs={6}>
                <ControlledTextField
                  startAdornment={<ResponsiveImage.FC urlSet={imgsPassword} />}
                  isDisabled={loading}
                  fieldKey="password"
                  label="Password"
                  type="password"
                  formControl={form.control} />
              </Grid>

              <Grid
                item
                xs={6}>
                <ControlledTextField
                  startAdornment={<ResponsiveImage.FC urlSet={imgsPassword} />}
                  isDisabled={loading}
                  fieldKey="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  formControl={form.control} />
              </Grid>
            </Grid>

            <Grid
              item
              ml={9}
              mt={1}>
              <PasswordRequirementsTypography>
                Your password must include at least 8 characters, 1 capital letter, 1 number,
              </PasswordRequirementsTypography>

              <PasswordRequirementsTypography>
                and 1 special character.
              </PasswordRequirementsTypography>
            </Grid>
          </Grid>

          <Grid item>
            <Button
              type="submit"
              disabled={loading}>
              Next
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseOnboardingTemplate.FC>
  );
};
